import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Send, Copy, Check, RotateCw, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Shared streaming chat panel.
 *
 * Props:
 *  - messages: [{id, role, content}]           — history
 *  - streaming: bool                           — is the assistant currently streaming?
 *  - streamingText: string                     — the in-flight assistant chunk
 *  - onSend: (message) => Promise<void>        — send handler (parent owns state + streaming)
 *  - onRegenerate: () => void                  — regenerate last assistant turn
 *  - placeholder: string
 *  - readyToGenerate: bool                     — writer-only: show "Generate draft" cta
 *  - onFinalize: () => void                    — writer-only: finalize handler
 *  - header: React.Node                        — optional header
 *  - suggestions: string[]                     — quick-reply chips shown when history is empty
 */
const AiChatPanel = ({
  messages,
  streaming,
  streamingText,
  onSend,
  onRegenerate,
  placeholder = "Type a message…",
  readyToGenerate = false,
  onFinalize,
  header = null,
  suggestions = [],
}) => {
  const [input, setInput] = useState("");
  const scrollerRef = useRef(null);
  const textareaRef = useRef(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, streamingText]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const value = input.trim();
    if (!value || streaming) return;
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    try { await onSend(value); } catch (err) { toast.error(err?.message || "Send failed"); }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const autosize = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 180) + "px";
  };

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id); setTimeout(() => setCopiedId(null), 1500);
      toast.success("Copied");
    } catch { toast.error("Clipboard blocked"); }
  };

  return (
    <div className="flex h-full flex-col bg-cream-100" data-testid="ai-chat-panel">
      {header}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-4">
        {messages.length === 0 && suggestions.length > 0 && (
          <div className="mx-auto max-w-2xl text-center pt-8">
            <div className="inline-grid place-items-center h-12 w-12 rounded-2xl bg-navy-800 text-white mb-4">
              <Sparkles className="h-6 w-6 text-saffron-400" />
            </div>
            <h3 className="font-display text-2xl font-bold text-navy-800">How can I help?</h3>
            <p className="mt-1 text-sm text-navy-500">Try one of these, or ask anything.</p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => onSend(s)}
                  disabled={streaming}
                  className="text-sm rounded-full border border-warm-200 bg-white px-3.5 py-2 text-navy-700 hover:border-saffron-300 hover:text-saffron-700 transition-colors disabled:opacity-60"
                  data-testid="ai-suggestion-chip"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <div key={m.id} data-testid={`ai-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-[1.7] ${
              m.role === "user"
                ? "bg-navy-800 text-white shadow-navy-soft"
                : "bg-white border border-warm-200 text-navy-800 shadow-warm"
            }`}>
              {m.role === "assistant" ? (
                <>
                  <div className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-navy-800 prose-headings:tracking-tight prose-p:my-2 prose-strong:text-navy-800 prose-a:text-saffron-600">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                  </div>
                  <div className="mt-2 flex items-center gap-1">
                    <button onClick={() => copy(m.content, m.id)} className="p-1.5 rounded-lg text-navy-400 hover:text-saffron-600 hover:bg-warm-100 transition" aria-label="Copy" data-testid="ai-msg-copy">
                      {copiedId === m.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </>
              ) : (
                <span className="whitespace-pre-wrap">{m.content}</span>
              )}
            </div>
          </div>
        ))}

        {streaming && (
          <div className="flex justify-start" data-testid="ai-streaming">
            <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-white border border-warm-200 text-navy-800 shadow-warm">
              {streamingText ? (
                <div className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-navy-800 prose-p:my-2">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingText}</ReactMarkdown>
                </div>
              ) : (
                <div className="flex gap-1.5" data-testid="ai-typing-indicator">
                  <span className="h-2 w-2 rounded-full bg-saffron-500 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 rounded-full bg-saffron-500 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-2 w-2 rounded-full bg-saffron-500 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {readyToGenerate && (
        <div className="px-4 sm:px-6 pb-2">
          <button
            onClick={onFinalize}
            data-testid="ai-writer-finalize-btn"
            className="btn-saffron w-full rounded-2xl h-11 font-semibold inline-flex items-center justify-center gap-2"
          >
            <Sparkles className="h-4 w-4" /> Generate my full campaign draft
          </button>
        </div>
      )}

      <form onSubmit={submit} className="border-t border-warm-200 bg-white/70 backdrop-blur px-4 sm:px-6 py-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={autosize}
            onKeyDown={handleKey}
            placeholder={placeholder}
            rows={1}
            disabled={streaming}
            data-testid="ai-input"
            className="flex-1 resize-none rounded-2xl border border-warm-200 bg-white px-4 py-2.5 text-sm text-navy-800 placeholder:text-navy-400 focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition min-h-[44px] max-h-[180px]"
          />
          {onRegenerate && messages.some((m) => m.role === "assistant") && !streaming && (
            <button
              type="button"
              onClick={onRegenerate}
              data-testid="ai-regenerate-btn"
              className="grid place-items-center h-11 w-11 rounded-2xl border border-warm-200 text-navy-600 hover:text-saffron-600 hover:border-saffron-300 transition"
              aria-label="Regenerate"
              title="Regenerate last answer"
            >
              <RotateCw className="h-4 w-4" />
            </button>
          )}
          <button
            type="submit"
            disabled={streaming || !input.trim()}
            data-testid="ai-send-btn"
            className="btn-saffron grid place-items-center h-11 w-11 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default AiChatPanel;
